import { zipSync } from 'fflate';
import type { EditorDocument } from '../core/model';
import type { Renderer } from '../render/renderer';
import { serializeProject } from '../persistence/project-format';
import type { DocumentSession, SessionFileHandle } from '../core/session';

function safeName(name: string): string {
  return name.replace(/[\\/:*?"<>|]+/g, '-').trim() || 'Untitled';
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

export interface SaveProjectResult {
  revision: number;
  durable: boolean;
  method: 'file-system-access' | 'download-copy';
}

type SavePickerWindow = Window & typeof globalThis & {
  showSaveFilePicker?: (options: {
    suggestedName: string;
    types: { description: string; accept: Record<string, string[]> }[];
  }) => Promise<SessionFileHandle>;
};

const saveTails = new WeakMap<DocumentSession, Promise<void>>();

export async function saveMaipoProject(session: DocumentSession, signal?: AbortSignal, saveAs = false): Promise<SaveProjectResult> {
  const captured = session.captureRevision();
  const predecessor = saveTails.get(session) ?? Promise.resolve();
  let releaseTurn: () => void = () => undefined;
  const turn = new Promise<void>((resolve) => { releaseTurn = () => resolve(); });
  const tail = predecessor.catch(() => undefined).then(() => turn);
  saveTails.set(session, tail);
  try {
    const checkCancelled = () => {
      if (signal?.aborted) throw new DOMException('The save was cancelled.', 'AbortError');
    };
    checkCancelled();
    const picker = window as SavePickerWindow;
    // Open while the initiating click/key gesture is still active. Compression
    // and an earlier save can take longer than the browser's activation window.
    const destination = !saveAs && session.fileHandle
      ? Promise.resolve(session.fileHandle)
      : picker.showSaveFilePicker
        ? picker.showSaveFilePicker({
          suggestedName: `${safeName(captured.document.name)}.maipo`,
          types: [{ description: 'Maipo project', accept: { 'application/vnd.maipo.project+zip': ['.maipo'] } }],
        })
        : Promise.resolve(undefined);
    // Snapshot synchronously, before waiting for the picker. Promise.all observes
    // both failures even when cancellation races compression.
    const [handle, bytes] = await Promise.all([destination, serializeProject(captured.document, signal)]);
    await predecessor.catch(() => undefined);
    checkCancelled();
    if (handle) {
      const writable = await handle.createWritable();
      try {
        checkCancelled();
        await writable.write(bytes);
        checkCancelled();
        await writable.close();
      } catch (error) {
        await writable.abort?.().catch(() => undefined);
        throw error;
      }
      session.fileHandle = handle;
      session.markSaved(captured.revision);
      return { revision: captured.revision, durable: true, method: 'file-system-access' };
    }
    downloadBlob(new Blob([bytes], { type: 'application/vnd.maipo.project+zip' }), `${safeName(captured.document.name)}.maipo`);
    return { revision: captured.revision, durable: false, method: 'download-copy' };
  } finally {
    releaseTurn();
    if (saveTails.get(session) === tail) saveTails.delete(session);
  }
}

function canvasBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('The browser could not encode the image.')), type, quality);
  });
}

/**
 * Encodes the flattened document exactly as "Export flattened PNG/JPEG" does.
 * The agent API's export (ADR-009 §4) calls this too, so both produce the same
 * bytes for the same revision in the same browser.
 */
export async function encodeFlattened(
  editorDocument: EditorDocument,
  renderer: Renderer,
  format: 'png' | 'jpeg',
  quality = 0.92,
  scale = 1,
): Promise<{ blob: Blob; width: number; height: number }> {
  let canvas = scale === 1 ? renderer.flatten(editorDocument) : renderer.flatten(editorDocument, scale);
  const type = format === 'png' ? 'image/png' : 'image/jpeg';
  if (format === 'jpeg') {
    const opaque = globalThis.document.createElement('canvas');
    opaque.width = canvas.width;
    opaque.height = canvas.height;
    const context = opaque.getContext('2d');
    if (!context) throw new Error('Canvas 2D is unavailable.');
    context.fillStyle = '#fff';
    context.fillRect(0, 0, opaque.width, opaque.height);
    context.drawImage(canvas, 0, 0);
    canvas = opaque;
  }
  const blob = await canvasBlob(canvas, type, format === 'jpeg' ? quality : undefined);
  return { blob, width: canvas.width, height: canvas.height };
}

export async function exportFlattened(
  editorDocument: EditorDocument,
  renderer: Renderer,
  format: 'png' | 'jpeg',
  quality = 0.92,
): Promise<void> {
  const { blob } = await encodeFlattened(editorDocument, renderer, format, quality);
  downloadBlob(blob, `${safeName(editorDocument.name)}.${format === 'jpeg' ? 'jpg' : 'png'}`);
}

/**
 * Flattens every document to PNG and delivers them as ONE zip download.
 * A single download survives browser multiple-download blocking, so either
 * all exports reach the user or the failure is real and visible.
 */
export async function exportFlattenedZip(documents: readonly EditorDocument[], renderer: Renderer, archiveName: string): Promise<number> {
  const entries: Record<string, Uint8Array> = {};
  const used = new Set<string>();
  for (const editorDocument of documents) {
    const blob = await canvasBlob(renderer.flatten(editorDocument), 'image/png');
    const base = safeName(editorDocument.name);
    let name = `${base}.png`;
    for (let counter = 2; used.has(name); counter += 1) name = `${base}-${counter}.png`;
    used.add(name);
    entries[name] = new Uint8Array(await blob.arrayBuffer());
  }
  const archive = zipSync(entries, { level: 0 });
  downloadBlob(new Blob([archive], { type: 'application/zip' }), `${safeName(archiveName)}.zip`);
  return documents.length;
}
